import { create, UseBoundStore, useStore } from 'zustand';
import React, { createContext, useContext, useLayoutEffect, RefObject } from 'react';
import { immer } from 'zustand/middleware/immer';
import { original, WritableDraft } from 'immer';
import { throttle } from 'lodash-es';
import EventEmitter3 from 'eventemitter3';

import { SelectSupplierInfo, SubMsgCompActions } from '../types/comp-block';
import { MessageWithTime, calculateMessageTimestamps } from '@/utils/message-timer';
import {
  IApiEmailMsgRegenerate,
  IApiMessageDto,
  IApiSessionMsgCreateReq,
  IApiSessionMsgListResp,
  IApiSubMessageDto,
} from '@/services/swagger/easourcing/models';
import { Notification } from '@/utils/notification/notification';
import { InquiryDialogProps } from '@/components/inquiry-dialog';
import { SSEConnection, sseRequest } from '@/services/api/sseV2';
import {
  messageCreatePost,
  messageListAfterPost,
  messageListAroundPost,
  messageListPost,
  stopPost,
} from '@/services/swagger/easourcing/SessionService';
import { appendMsgChunkToAiMsg, initMsgDto, MsgSenderRole, MsgStreamChunk, MsgStreamFinished } from '../types';
import { PushEventType, usePushMessage } from '@/hooks/useWebSocket';
import { NewMessagePayload } from '@/layouts/main-layout/sider/hooks/useRequestsUpdate';

// ─── Events & Notifications ───────────────────────────────────────────────────

export const chatEmitter = new EventEmitter3();

type InquiryCallbackPayload = Parameters<Required<InquiryDialogProps>['onSuccess']>[0];

export interface ChatEvents {
  copy: void;
  sendInquirySuccess: InquiryCallbackPayload;
}

export const chatNotification = new Notification<ChatEvents>();

// ─── Types ────────────────────────────────────────────────────────────────────

type MsgLocationInfo = Partial<Pick<IApiSessionMsgListResp, 'lastCreateTime' | 'lastId'>> & {
  hasMore?: boolean;
};

export type MsgListParams = {
  /** oldest: 向旧方向翻页；newest: 向新方向翻页 */
  direction: 'oldest' | 'newest';
  isAll?: boolean;
};

type ScrollContext = {
  scrollHeight: number;
  clientHeight: number;
  scrollTop: number;
};

/**
 * 滚动意图：由 store actions 写入，由 hook 的 useLayoutEffect 消费后清除
 */
type ScrollIntent =
  | { type: 'smooth-bottom' }
  | { type: 'to-element'; messageId: string }
  | { type: 'preserve-distance'; scrollContext: ScrollContext }
  | null;

// ─── Store State ──────────────────────────────────────────────────────────────

interface ChatPageState {
  contactId?: string;
  /** 允许自动打开详情面板 */
  allowAutoOpenDetail: boolean;
  /** 当前会话 id */
  sessionId: string;
  /** true 正在执行提问接口请求 */
  asking: boolean;
  /** true 正在执行加载回复接口请求 */
  isLoadingResponse: boolean;
  /** key 为 reportId，value 为该报告详情是否正在加载中 */
  reportDetailLoadingMap: Record<string, boolean>;
  /** 在会话生命周期中，通过 SSE 接收的消息 id 集合 */
  sseMessageIds: Set<string>;
  isOpenAutoClick: boolean;

  /** 当前正在处理的 SSE 消息 id */
  currentLoadingSseMessageId: string;
  /**
   *  当前会话中最新一条 SSE 消息 id，只有开启新的 SSE 连接时才会更新
   */
  lastestSseMessageId: string;
  /** 当前页面展示的消息列表（含时间戳装饰） */
  msgList: MessageWithTime<IApiMessageDto>[];
  /** 已选供应商信息，key 为 subMessageId */
  selectedSuppliers: Record<string, SelectSupplierInfo>;

  // ── 分页定位状态（原 hook 中的 refs）──
  /** 分页请求防并发锁，请求进行中为 true */
  loadingMoreLock: boolean;
  /** 当前列表中最旧一条消息的分页游标，用于向旧方向翻页 */
  oldLatestMsg: MsgLocationInfo | undefined;
  /** 当前列表中最新一条消息的分页游标，用于向新方向翻页 */
  newLatestMsg: MsgLocationInfo | undefined;

  // ── 滚动状态（原 hook 中的 refs）──
  /** 待执行的滚动意图，由 actions 写入，useLayoutEffect 消费后清除 */
  scrollIntent: ScrollIntent;

  /**
   * 是否展示骨架屏，初始为 true，首次接口请求完毕后设置为 false，后续始终保持 false
   */
  showSkeleton: boolean;

  // ── 原 store actions ──
  /** 通用状态更新，接受 immer updater 函数直接修改 draft state */
  addSelectedSupplier: (subMessageId: string, supplierInfo: SelectSupplierInfo) => void;
  /** 删除指定 subMessageId 对应的已选供应商 */
  deleteSelectedSupplier: (subMessageId: string) => void;
  /** 重置会话相关状态，清空消息列表、分页游标、滚动意图等 */
  reset: () => void;
  /** 通用状态更新：接受 immer updater 函数，直接修改 draft state */
  setChatPageState: (updater: (prev: WritableDraft<ChatPageState>) => void) => void;
  /** 按 messageId 更新单条消息，通过 updater 函数返回新值 */
  updateMsgItem: (
    subMessageId: string,
    updater: (prev: MessageWithTime<IApiMessageDto>) => MessageWithTime<IApiMessageDto>
  ) => void;
  /** 清空所有已选供应商 */
  clearSelectedSuppliers: () => void;
  /** 获取当前完整 store state 快照（用于在闭包外读取最新状态） */
  getStoreState: () => ChatPageState;
  /** 直接合并设置 store state（浅合并） */
  setStoreState: (state: Partial<ChatPageState>) => void;
  /** 将 messageId 加入 SSE 消息 id 集合 */
  addSseMessageId: (messageId: string) => void;

  /**
   * 将新消息追加或前插到消息列表
   * @param messageList 新消息数组
   * @param position 'after'（默认）追加到末尾；'before' 前插到头部
   */
  setMsgList: (messageList: MessageWithTime<IApiMessageDto>[], position?: 'before' | 'after') => void;
  /** 拉取当前列表末尾之后的所有新消息并追加，供 WebSocket 推送时调用 */
  getAllNewMsgList: () => Promise<void>;
  /** 以目标 messageId 为中心加载前后一页消息，并设置定位滚动意图 */
  getMsgListByLocate: (messageId: string) => Promise<void>;
  /** 分页加载消息列表，支持 oldest/newest 两个方向，首次加载时自动恢复未完成的 SSE */
  fetchMsgList: (params: MsgListParams) => Promise<void>;
  /** 发送用户消息，写入列表后自动触发 loadResponse 等待 AI 回复 */
  ask: (content: string) => Promise<void>;
  /** 通过完整的 messageCreatePost 入参发送消息，写入列表后自动触发 loadResponse */
  askWithParams: (params: Omit<IApiSessionMsgCreateReq, 'sessionId'>) => Promise<void>;
  /**
   * AI 写信：基于编辑器当前内容（subject/content）向 AI 发起写信请求
   * @param editorContent 编辑器内容，包含 subject 和 content
   */
  askEmailWrite: () => Promise<void>;
  /**
   * AI 重新生成信件：基于原消息 ID 向 AI 发起重新生成请求
   * @param originMessageId 原消息 ID
   * @param originSubMessageId 原子消息 ID
   */
  askEmailRegenerate: (params: IApiEmailMsgRegenerate) => Promise<void>;
  /** 建立 SSE 连接接收 AI 流式回复，MESSAGE 事件初始化占位，SUB_MESSAGE 事件追加 chunk */
  loadResponse: (params: { preMsgId?: string; lastMsgId?: string }) => void;
  /** 中止当前 AI 回复：通知服务端、断开 SSE、将消息标记为 stopped */
  abort: () => Promise<void>;
  /** 设置是否阻止自动滚动到底部 */
  setPreventScrollBottom: (value: boolean) => void;
  /** 立即滚动到消息列表底部，可选是否平滑滚动（默认平滑） */
  scrollToBottom: (smooth?: boolean) => void;
  /** 强制滚动到消息列表底部，忽略 preventScrollBottom 标志，可选是否平滑滚动（默认平滑） */
  scrollToBottomForce: (smooth?: boolean) => void;
  /** 清除待执行的滚动意图（由 useLayoutEffect 执行滚动后调用） */
  clearScrollIntent: () => void;
  /** 返回消息列表容器 DOM ref */
  getListWrapperRef: () => RefObject<HTMLDivElement>;
  /** 滚动事件处理（节流），读取 DOM 状态并更新防滚动底部标志、触发旧消息加载 */
  handleScroll: () => void;
  /** wheel 事件处理，监听用户主动滚动行为：向上滚动时阻止自动滚底，到达底部时恢复自动滚底 */
  handleWheel: (e: React.WheelEvent<HTMLDivElement>) => void;

  onSomeAtion: (action: string, payload: unknown) => Promise<void>;
  /**
   * 将 insight chip sub message 插入到指定消息（messageId）的 subMessage 列表末尾。
   * 如果该消息已存在 INSIGHT_CHIP 类型的 sub message，则跳过（幂等保护）。
   */
  insertInsightChipSubMsg: (messageId: string, subMsg: IApiSubMessageDto) => void;
  /**
   * 将搜索卡片 submessage 更新到指定消息的 subMessage 列表中。
   * 用于 modify-search-card 轮询完成后，将新的 cardData 替换到对应 subMessage 条目。
   */
  updateSearchCardSubMsg: (messageId: string, subMessageId: string, newSubMsg: IApiSubMessageDto) => void;

  /** 输入框上方 tooltip 是否可见（报告详情展开后 5s 自动隐藏） */
  showInputTooltip: boolean;
  /** 触发 tooltip 显示，5s 后自动隐藏；重复调用会重置计时 */
  triggerInputTooltip: () => void;
  /** 立即隐藏 tooltip 并清除计时器 */
  hideInputTooltip: () => void;
}

// ─── Store Factory ────────────────────────────────────────────────────────────

/**
 * 创建 chatPage 状态管理 store
 */
export const createChatPageStore = <T extends SubMsgCompActions>(
  initParams: Partial<Pick<ChatPageState, 'sessionId' | 'selectedSuppliers' | 'contactId' | 'allowAutoOpenDetail'>> & {
    listWrapperRef: RefObject<HTMLDivElement>;
    onSomeAtion?: (action: keyof T, payload: T[keyof T]) => void;
  }
) => {
  // SSE 连接实例：作为闭包变量管理，避免 immer 代理类实例
  let sseConnection: SSEConnection | null = null;
  // 列表容器 DOM ref：不放入 immer state 避免被 freeze，通过 getListWrapperRef 暴露
  const listWrapperRef = initParams.listWrapperRef;
  // 上一次滚动位置，用于判断滚动方向，闭包变量避免触发 immer 更新
  let prevScrollTop = 200;

  let preventScrollBottom = false;

  // 输入框 tooltip 自动隐藏计时器（闭包变量，不放入 immer state）
  let inputTooltipTimer: ReturnType<typeof setTimeout> | null = null;

  return create<ChatPageState>()(
    immer((set, get) => {
      // let isUserScrolling: boolean = false;

      /** 节流滚动到底部：100ms 内最多执行一次，避免频繁触发 */
      const scrollToBottomThrottled = throttle(
        (smooth = true) => {
          const el = listWrapperRef.current;
          if (!el) return;
          if (preventScrollBottom) {
            return;
          }
          el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'instant' });
        },
        100,
        { leading: true, trailing: true }
      );

      /** 节流强制滚动到底部：忽略 preventScrollBottom 标志，100ms 内最多执行一次 */
      const scrollToBottomForceThrottled = throttle(
        (smooth = true) => {
          const el = listWrapperRef.current;
          if (!el) return;
          preventScrollBottom = false;
          el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'instant' });
        },
        100,
        { leading: true, trailing: true }
      );

      /** 节流滚动处理：更新防滚底部标志，到顶时触发加载旧消息 */
      const handleScroll = throttle(
        () => {
          const el = listWrapperRef.current;
          if (!el) return;
          const scrollTop = el.scrollTop || 0;
          if (scrollTop <= 300 && prevScrollTop > 300) {
            // 如果还有更多旧消息可加载
            if (get().oldLatestMsg?.lastId) {
              get().fetchMsgList({ direction: 'oldest' });
            }
          }
          prevScrollTop = scrollTop;
        },
        100,
        { leading: true, trailing: false }
      );

      return {
        // ── 初始状态 ──
        sessionId: initParams.sessionId || '',
        contactId: initParams.contactId || '',
        allowAutoOpenDetail: initParams.allowAutoOpenDetail ?? true,
        showSkeleton: true,
        isOpenAutoClick: false,
        msgList: [],
        asking: false,
        isLoadingResponse: false,
        reportDetailLoadingMap: {},
        currentLoadingSseMessageId: '',
        lastestSseMessageId: '',
        sseMessageIds: new Set(),
        selectedSuppliers: initParams.selectedSuppliers || {},
        loadingMoreLock: false,
        oldLatestMsg: undefined,
        newLatestMsg: undefined,
        preventScrollBottom: false,
        scrollIntent: null,
        showInputTooltip: false,

        // ── 原 store actions ──

        /** 通用状态更新：接受 immer updater 函数，直接修改 draft state */
        setChatPageState: updater => set(updater),

        /** 按 messageId 更新单条消息，通过 updater 函数返回新值 */
        updateMsgItem: (subMessageId, updater) => {
          set({
            msgList: get().msgList.map(item => (item.messageId === subMessageId ? updater(item) : item)),
          });
        },

        /** 新增已选供应商：以 subMessageId 为 key 写入 selectedSuppliers */
        addSelectedSupplier: (subMessageId, supplierInfo) => {
          set({ selectedSuppliers: { ...get().selectedSuppliers, [subMessageId]: supplierInfo } });
        },

        /** 删除指定 subMessageId 对应的已选供应商 */
        deleteSelectedSupplier: subMessageId => {
          const selectedSuppliers = { ...get().selectedSuppliers };
          delete selectedSuppliers[subMessageId];
          set({ selectedSuppliers });
        },

        /** 重置会话相关状态，清空消息列表、分页定位、滚动意图等 */
        reset: () =>
          set({
            sessionId: '',
            msgList: [],
            selectedSuppliers: {},
            loadingMoreLock: false,
            oldLatestMsg: undefined,
            newLatestMsg: undefined,
            scrollIntent: null,
          }),

        /** 清空所有已选供应商 */
        clearSelectedSuppliers: () => set({ selectedSuppliers: {} }),

        /** 获取当前完整 store state 快照（用于在闭包外读取最新状态） */
        getStoreState: () => get(),

        /** 直接合并设置 store state（浅合并） */
        setStoreState: state => set(state),

        /** 将 messageId 加入 SSE 消息 id 集合，用于标记本次会话中通过 SSE 收到的消息 */
        addSseMessageId: messageId => {
          set({ sseMessageIds: new Set([...get().sseMessageIds, messageId]) });
        },

        /** 触发输入框 tooltip 显示，5s 后自动隐藏；重复调用会重置计时 */
        triggerInputTooltip: () => {
          if (inputTooltipTimer !== null) {
            clearTimeout(inputTooltipTimer);
            inputTooltipTimer = null;
          }
          set({ showInputTooltip: true });
          inputTooltipTimer = setTimeout(() => {
            inputTooltipTimer = null;
            set({ showInputTooltip: false });
          }, 5000);
        },

        /** 立即隐藏 tooltip 并清除计时器 */
        hideInputTooltip: () => {
          if (inputTooltipTimer !== null) {
            clearTimeout(inputTooltipTimer);
            inputTooltipTimer = null;
          }
          set({ showInputTooltip: false });
        },

        // ── 新增 actions ──

        setMsgList: (messageList, position = 'after') => {
          set(prev => {
            const prevList = original<MessageWithTime<IApiMessageDto>[]>(prev.msgList) || [];

            // 收集 prevList 所有 messageId，存到 Set 中，用于去重判断
            const prevMessageIdSet = new Set(prevList.map(item => item.messageId));

            const normalized = messageList
              .map(item =>
                item.streamFinished === MsgStreamFinished.unfinished
                  ? { ...item, subMessage: item.subMessage || [] }
                  : item
              )
              // 过滤掉已存在于 prevList 中的消息，避免重复
              .filter(item => !prevMessageIdSet.has(item.messageId));

            const list = position === 'after' ? [...prevList, ...normalized] : [...normalized, ...prevList];

            prev.msgList = calculateMessageTimestamps([
              ...list,
              // ── 行业报告卡片 mock（Market Trend）──
            ]);
            //   prev.msgList = calculateMessageTimestamps([
            //     ...list,
            //     {
            //       messageId: '691651507262681187',
            //       senderRole: 'ASSISTANT',
            //       createTime: 1772691368000,
            //       like: 0,
            //       streamFinished: 1,
            //       subMessage: [
            //         {
            //           subMessageId: '691651507799552094',
            //           contentType: 'REPLY',
            //           contentValue: {
            //             text: '{{contactName}} ({{contactEmail}}) of {{companyName}} has responded to our request for {{quantity}} of {{productName}}，auto-inquiry to {{companyName}} has stopped. Please resume manually if needed.',
            //             contactEmailMsgId: '691651505022922792',
            //             companyId: 'silverstonetek.com',
            //             contactId: '1581',
            //             contactName: 'rebate',
            //             contactEmail: 'wb.pingzhihua@office.163.com',
            //             companyName: 'SILVERSTONE TECHNOLOGY CO., LTD.',
            //             quantity: 100,
            //             productName: '轮滑鞋',
            //             inquiryTaskId: '691291850430050398',
            //           },
            //         },
            //       ],
            //     },
            //     {
            //       messageId: '691651507262681187-1',
            //       senderRole: 'ASSISTANT',
            //       createTime: 1772691368000,
            //       like: 0,
            //       streamFinished: 1,
            //       subMessage: [
            //         {
            //           subMessageId: '691651507799552094',
            //           contentType: 'TASK_COMPLETE',
            //           contentValue: {
            //             text: 'Inquiry task "{{taskName}}" is complete',
            //             taskName: '哈哈任务',
            //             inquiryTaskId: '691291850430050398',
            //           },
            //         },
            //       ],
            //     },
            //     // {
            //     //   messageId: '691651507262681187-2',
            //     //   senderRole: 'ASSISTANT',
            //     //   createTime: 1772691368000,
            //     //   like: 0,
            //     //   streamFinished: 1,
            //     //   subMessage: [
            //     //     {
            //     //       subMessageId: '691651507799552094',
            //     //       contentType: 'DRAFT_EMAIL',
            //     //       contentValue: {
            //     //         subject: 'Re: Inquiry Task Completion',
            //     //         content: 'Re: Inquiry Task Completion',
            //     //         summary: 'Re: Inquiry Task Completion',
            //     //       },
            //     //     },
            //     //   ],
            //     // },
            //   ]);
          });
        },

        getAllNewMsgList: async () => {
          // 取当前列表末尾消息 id 作为游标，拉取其之后的所有新消息并追加到列表末尾
          const { sessionId, msgList } = get();
          const messageId = msgList.length > 0 ? msgList[msgList.length - 1].messageId : undefined;
          if (!sessionId || !messageId) {
            console.warn('sessionId or messageId is empty');
            return;
          }
          const res = await messageListAfterPost({ sessionId, messageId });
          const { data } = res || {};
          const { messageList } = data || {};
          if (!messageList || messageList.length === 0) return;
          set({
            newLatestMsg: { lastCreateTime: data?.lastCreateTime, lastId: data?.lastId },
            scrollIntent: { type: 'smooth-bottom' },
          });
          get().setMsgList(messageList);

          const newMsgList = get().msgList;
          const lastMsg = newMsgList[newMsgList.length - 1];
          // 如果更新的消息为空，直接返回
          if (!lastMsg) return;
          // 如果更新的消息未完成，触发 loadResponse 恢复 SSE 接收
          if (lastMsg.streamFinished === MsgStreamFinished.unfinished) {
            get().loadResponse({ lastMsgId: lastMsg.messageId });
          }
        },

        getMsgListByLocate: async messageId => {
          // 以目标 messageId 为中心拉取前后一页消息，并设置滚动意图定位到该消息
          const { sessionId } = get();
          const res = await messageListAroundPost({ sessionId, messageId }).catch(err => {
            // 请求失败时隐藏骨架屏，避免骨架屏永久展示
            if (get().showSkeleton) {
              set({ showSkeleton: false });
            }
            throw err;
          });
          const { data } = res || {};
          const { messageList: _messageList } = data || {};
          if (!_messageList || _messageList.length === 0) {
            // 消息列表为空时，同样隐藏骨架屏
            if (get().showSkeleton) {
              set({ showSkeleton: false });
            }
            return;
          }
          set({
            newLatestMsg: { lastCreateTime: data?.lastCreateTime, lastId: data?.lastId },
            oldLatestMsg: { lastCreateTime: data?.firstCreateTime, lastId: data?.firstId },
            scrollIntent: { type: 'to-element', messageId },
          });
          get().setMsgList(_messageList);
          // 首次请求完毕后隐藏骨架屏，后续始终保持隐藏状态
          if (get().showSkeleton) {
            set({ showSkeleton: false });
          }
        },

        fetchMsgList: async params => {
          // 分页加载消息列表：支持向旧/新两个方向翻页，使用 loadingMoreLock 防止并发请求
          // 首次加载时若末尾消息未完成，自动触发 loadResponse 恢复 SSE 接收
          const state = get();
          if (state.loadingMoreLock) return;
          set({ loadingMoreLock: true });

          // 在 API 调用前捕获当前滚动位置，用于加载旧消息后还原位置
          const el = listWrapperRef.current;
          const scrollContext: ScrollContext | undefined = el
            ? { scrollHeight: el.scrollHeight, clientHeight: el.clientHeight, scrollTop: el.scrollTop }
            : undefined;

          const locationInfo = params.direction === 'oldest' ? state.oldLatestMsg : state.newLatestMsg;
          const res = await messageListPost({
            direction: params.direction === 'oldest' ? 0 : 1,
            sessionId: state.sessionId,
            lastCreateTime: locationInfo?.lastCreateTime,
            pageSize: params.isAll ? 1000 : 20,
            lastId: locationInfo?.lastId,
          }).catch(err => {
            // 请求失败时也隐藏骨架屏，避免骨架屏永久展示
            if (get().showSkeleton) {
              set({ showSkeleton: false });
            }
            set({ loadingMoreLock: false });
            throw err;
          });

          const { data } = res || {};
          if (params.direction === 'oldest') {
            set({ oldLatestMsg: { lastCreateTime: data?.lastCreateTime, lastId: data?.lastId } });
          } else {
            set({ newLatestMsg: { lastCreateTime: data?.lastCreateTime, lastId: data?.lastId } });
          }

          const { messageList } = data || {};
          if (!messageList || messageList.length === 0) {
            // 消息列表为空时，同样隐藏骨架屏（首次请求无数据的情况）
            if (get().showSkeleton) {
              set({ showSkeleton: false });
            }
            set({ loadingMoreLock: false });
            return;
          }

          const msgListIsEmpty = get().msgList.length === 0;
          if (scrollContext) {
            set({ scrollIntent: { type: 'preserve-distance', scrollContext } });
          }
          get().setMsgList(messageList, params.direction === 'oldest' ? 'before' : 'after');

          // 首次请求完毕后隐藏骨架屏，后续始终保持隐藏状态
          if (get().showSkeleton) {
            set({ showSkeleton: false });
          }

          if (msgListIsEmpty) {
            const lastMsg = messageList[messageList.length - 1];
            if (lastMsg.senderRole === MsgSenderRole.User) {
              get().loadResponse({ preMsgId: lastMsg.messageId });
            } else if (lastMsg.streamFinished === MsgStreamFinished.unfinished) {
              get().loadResponse({ lastMsgId: lastMsg.messageId });
            }
          }
          set({ loadingMoreLock: false });
        },

        askWithParams: async params => {
          // 通过完整入参发送消息：asking/isLoadingResponse 任一为 true 时阻止重复提交
          const { asking, isLoadingResponse, sessionId, contactId } = get();
          if (asking || isLoadingResponse) {
            console.warn('there is a asking or loading response, please wait');
            return;
          }
          set({ asking: true });
          const res = await messageCreatePost({ ...params, sessionId, contactId }).catch(err => {
            set({ asking: false });
            throw err;
          });
          get().setMsgList([res.data.messageDto]);
          get().loadResponse({ preMsgId: res.data.messageId });
          preventScrollBottom = false;
          set({ asking: false, scrollIntent: { type: 'smooth-bottom' } });
        },

        ask: async content => {
          // 直接调用 askWithParams，传入 question 参数
          await get().askWithParams({ question: content });
        },

        askEmailWrite: async () => {
          // AI 写信：携带编辑器内容，questionType 固定为 WRITE_EMAIL
          await get().askWithParams({
            questionType: 'AI_WRITE',
          });
        },

        askEmailRegenerate: async params => {
          // AI 重新生成信件：携带原消息 ID，questionType 固定为 REGENERATE_EMAIL
          await get().askWithParams({
            questionType: 'REGENERATE',
            emailMsgRegenerate: params,
          });
        },

        loadResponse: params => {
          // 建立 SSE 连接，实时接收 AI 回复流：
          // - MESSAGE 事件：初始化一条空的 AI 消息占位
          // - SUB_MESSAGE 事件：将 chunk 数据追加到对应消息的 subMessage 中
          // 每次收到数据时，若用户未手动上滑则自动滚动到底部
          const { isLoadingResponse, sessionId } = get();
          if (isLoadingResponse) return;
          sseConnection?.disconnect();
          set({ isLoadingResponse: true });

          let currentMessageId = '';
          console.log(1111, 'session/start api log', '接口调用之前显示', params, sessionId);

          sseConnection = sseRequest({
            url: '/es/api/pub/session/start',
            method: 'POST',
            data: { preMsgId: params.preMsgId, lastMsgId: params.lastMsgId, sessionId },
            enableQueue: true,
            queueInterval: 50,
            onStart() {
              console.log(1111, 'session/start api log', 'onStart');
            },
            onError: () => {
              console.log(1111, 'session/start api log', 'onError');
              set({ isLoadingResponse: false, currentLoadingSseMessageId: '' });
              sseConnection = null;
            },
            onFinish: () => {
              console.log(1111, 'session/start api log', 'onFinish');
              set({ isLoadingResponse: false, currentLoadingSseMessageId: '' });
              sseConnection = null;
            },
            onMessage: (data: MsgStreamChunk) => {
              try {
                if (data.event === 'MESSAGE') {
                  currentMessageId = data.data.messageId;
                  set({ currentLoadingSseMessageId: data.data.messageId, lastestSseMessageId: data.data.messageId });
                  get().addSseMessageId(data.data.messageId);
                  const isExist = get().msgList.some(item => item.messageId === data.data.messageId);
                  if (!isExist) {
                    get().setMsgList([initMsgDto(data.data.messageId)]);
                  }
                } else if (data.event === 'SUB_MESSAGE_CHUNK') {
                  console.log(1111, 'session/start api log', 'SUB_MESSAGE_CHUNK', data);
                  // 后端流式 chunk 到达，立即追加文本并触发 React 重渲染
                  set(prev => {
                    const index = prev.msgList.findIndex(item => item.messageId === currentMessageId);
                    if (index === -1) return;
                    const newMsg = appendMsgChunkToAiMsg(prev.msgList[index], data);
                    if (newMsg) {
                      prev.msgList[index] = newMsg;
                    }
                  });
                } else if (data.event === 'SUB_MESSAGE') {
                  // 完整子消息到达，覆盖已累积的 chunk 文本（后端权威数据）
                  set(prev => {
                    const index = prev.msgList.findIndex(item => item.messageId === currentMessageId);
                    if (index === -1) return;
                    const newMsg = appendMsgChunkToAiMsg(prev.msgList[index], data);
                    if (newMsg) {
                      prev.msgList[index] = newMsg;
                    }
                  });
                }
              } catch (error) {
                console.error(1111, 'session/start api log', 'handle onMessage error:', error);
              }
              scrollToBottomThrottled();
            },
          });
        },

        abort: async () => {
          // 中止当前 AI 回复：调用 stop 接口通知服务端，断开 SSE 连接，并将消息标记为 stopped
          const { currentLoadingSseMessageId, sessionId } = get();
          if (!currentLoadingSseMessageId) {
            console.error('currentLoadingSseMessageId is empty');
            return;
          }
          await stopPost({ sessionId, messageId: currentLoadingSseMessageId });
          sseConnection?.disconnect();
          sseConnection = null;
          set(prev => {
            const msg = prev.msgList.find(item => item.messageId === currentLoadingSseMessageId);
            if (msg) {
              msg.streamFinished = MsgStreamFinished.stopped;
            }
            prev.isLoadingResponse = false;
            prev.currentLoadingSseMessageId = '';
          });
        },

        async onSomeAtion(action, payload) {
          if (action === 'sendChipMessage') {
            const { text } = payload as { text: string };
            get().ask(text);
            return;
          }
          await initParams?.onSomeAtion?.(action as unknown as keyof T, payload as T[keyof T]);
        },
        /** 设置是否阻止自动滚动到底部（用户上滑时为 true，滚回底部时为 false） */
        setPreventScrollBottom: value => {
          preventScrollBottom = value;
        },

        /** 节流滚动到消息列表底部，smooth 为 true 时平滑滚动（默认），false 时瞬间跳转 */
        scrollToBottom: scrollToBottomThrottled,

        /** 强制节流滚动到消息列表底部，忽略 preventScrollBottom 标志 */
        scrollToBottomForce: scrollToBottomForceThrottled,

        /** 清除待执行的滚动意图（由 useLayoutEffect 执行滚动后调用） */
        clearScrollIntent: () => set({ scrollIntent: null }),

        /** 返回消息列表容器 DOM ref（避免 immer freeze，从闭包返回） */
        getListWrapperRef: () => listWrapperRef,

        /** 节流滚动处理（从闭包引用，函数本身不在 immer 管理范围内） */
        handleScroll,

        /**
         * wheel 事件处理：监听用户主动滚动行为
         * - deltaY < 0（向上滚动）：设置 preventScrollBottom = true，阻止 AI 回复自动滚底
         * - 滚动容器已到达底部（scrollBottom <= 10）：恢复 preventScrollBottom = false
         */
        handleWheel: (e: React.WheelEvent<HTMLDivElement>) => {
          const el = listWrapperRef.current;
          if (!el) return;
          const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
          if (e.deltaY < 0) {
            // 用户向上滚动，阻止自动滚动到底部
            get().setPreventScrollBottom(true);
          } else if (scrollBottom <= 10) {
            // 用户向下滚动且已到达底部，恢复自动滚动到底部
            get().setPreventScrollBottom(false);
          }
        },

        /**
         * 将 insight chip sub message 插入到指定消息的 subMessage 列表末尾。
         * 幂等保护：如果该消息已存在 INSIGHT_CHIP 类型的 sub message，则跳过不重复插入。
         */
        insertInsightChipSubMsg: (messageId, subMsg) => {
          set(draft => {
            const msg = draft.msgList.find(m => m.messageId === messageId);
            if (!msg) return;
            const alreadyExists = (msg.subMessage ?? []).some(sub => sub.contentType === 'INSIGHT_CHIP');
            if (alreadyExists) return;
            msg.subMessage = [...(msg.subMessage ?? []), subMsg];
          });
        },

        updateSearchCardSubMsg: (messageId: string, subMessageId: string, newSubMsg: IApiSubMessageDto) => {
          set(draft => {
            const msg = draft.msgList.find(m => m.messageId === messageId);
            if (!msg || !msg.subMessage) return;
            const idx = msg.subMessage.findIndex(s => s.subMessageId === subMessageId);
            if (idx === -1) return;
            msg.subMessage[idx] = newSubMsg;
          });
        },
      };
    })
  );
};

// ─── Context & Hook ───────────────────────────────────────────────────────────

/**
 * chatPage 状态管理 store 类型定义
 */
type ChatPageStore = ReturnType<typeof createChatPageStore>;

/**
 * chatPage 状态管理 store 上下文定义
 */
export const ChatPageContext = createContext<ChatPageStore | null>(null);

/**
 * 计算页面看见区域顶部位置距离滚动到底部的距离
 * @param el
 * @returns
 */
const calcScrollBottom = (el: Pick<ScrollContext, 'scrollHeight' | 'scrollTop'>) => {
  return el.scrollHeight - el.scrollTop;
};

/**
 * chatPage 状态管理 store 选择器定义
 */
export const useChatPageStore = (selector => {
  const store = useContext(ChatPageContext);
  if (!store) throw new Error('Missing ChatPageContext.Provider in the tree');
  return useStore(store, selector);
}) as UseBoundStore<ChatPageStore>;

// ─── Controller Hook ──────────────────────────────────────────────────────────
// 仅保留 React 生命周期副作用逻辑（scroll intent 消费 + WebSocket 新消息监听）

const useScrollIntentAndNewMsg = (params: {
  listWrapperRef: React.RefObject<HTMLDivElement>;
  scrollIntent: ScrollIntent;
  clearScrollIntent: () => void;
  sessionId: string;
  getAllNewMsgList: () => void;
}) => {
  const { listWrapperRef, scrollIntent, clearScrollIntent, sessionId, getAllNewMsgList } = params;

  // 消费滚动意图：在 DOM 更新后执行滚动，然后清除意图
  useLayoutEffect(() => {
    const el = listWrapperRef.current;
    if (!el || !scrollIntent) return;

    if (scrollIntent.type === 'smooth-bottom') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else if (scrollIntent.type === 'to-element') {
      document.querySelector(`[data-message-id="${scrollIntent.messageId}"]`)?.scrollIntoView({ behavior: 'smooth' });
    } else if (scrollIntent.type === 'preserve-distance') {
      const { scrollContext } = scrollIntent;

      const distanceToBottom = calcScrollBottom(scrollContext);
      const newScrollTop = el.scrollHeight - distanceToBottom;
      el.scrollTop = newScrollTop;
    }
    clearScrollIntent();
  }, [scrollIntent, clearScrollIntent, listWrapperRef]);

  // WebSocket 推送：有新消息时拉取
  usePushMessage<NewMessagePayload>(PushEventType.SESSION_NEW_MSG, payload => {
    const content = payload.content || {};
    if (content?.sessionId === sessionId) {
      getAllNewMsgList();
    }
  });
};

/**
 * 消费 store 中的滚动意图并监听 WebSocket 新消息推送。
 * DOM refs 及滚动/分页逻辑已全部迁移至 store，此 hook 仅处理 React 副作用。
 */
export const useResetMsgListScroll = () => {
  const scrollIntent = useChatPageStore(store => store.scrollIntent);
  const getAllNewMsgList = useChatPageStore(store => store.getAllNewMsgList);
  const clearScrollIntent = useChatPageStore(store => store.clearScrollIntent);
  const sessionId = useChatPageStore(store => store.sessionId);
  const getListWrapperRef = useChatPageStore(store => store.getListWrapperRef);

  useScrollIntentAndNewMsg({
    listWrapperRef: getListWrapperRef(),
    scrollIntent,
    clearScrollIntent,
    sessionId,
    getAllNewMsgList,
  });
};
